// Tools operate on objects on the canvas and can be controlled by keyboard
// shortcuts, toolbar icons, and menu items.
import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import Shortcut from '../utils/Shortcut';
import { makeTestId } from '../utils/tools';

export type ToolButtonComponent<
  O extends fabric.Object = fabric.Object,
  T extends typeof Tool = typeof Tool
> = React.FC<{
  selection: O;
  controller: WhiteboardController;
  tool: T;
  forceRecalculate?: () => void;
  className?: string;
}>;

type SimpleToolButtonComponent = React.FC;

const simpleToolButton = (
  tool: typeof Tool,
  icon: React.ReactNode,
  options: {
    stopPropagation?: boolean;
    className?: string;
  } = {}
): SimpleToolButtonComponent => {
  return ApplicationView(() => {
    const controller = useController(WhiteboardController);

    return (
      <ToolButton
        className={options.className}
        onClick={event => {
          controller.actionApplyTool(tool);

          if (options.stopPropagation) {
            event.stopPropagation();
          }
        }}
        disabled={!controller.isToolEnabled({ tool })}
        tool={tool}
      >
        {typeof icon === 'string' ? <aha-icon icon={icon} /> : icon}
      </ToolButton>
    );
  });
};

export interface IToolEnabledInput {
  controller: WhiteboardController;
  selection?: fabric.Object | null;
}
export type IToolAvailableInput = IToolEnabledInput;
export interface IToolActionInput {
  controller: WhiteboardController;
  selection: fabric.Object;
}
export interface IToolActionCompleteInput {
  controller: WhiteboardController;
}

// base tool including all common functionality. Only use directly for tools with custom actions
export abstract class BaseTool {
  // User visible name of the tool.
  static toolName = 'Tool name';

  // Shortcut for the tool.
  static shortcut = new Shortcut('');

  // Message to be shown when the user tries to use a disabled tool (usually
  // through keyboard shortcuts).
  static disabledMessage: string | null = null;

  // Whether this tool's shortcut key can be held down to repeat the action
  static allowsRepeat = false;

  // Check if this tool is available in the object toolbar when the object is
  // locked
  static availableWhenLocked = false;

  // Controls whether WhiteboardController.actionApplyTool should
  // refocus the whiteboard when the tool is applied (which also defaults
  // `true` if no value for this parameter is passed to actionApplyTool).
  static shouldRefocusWhiteboardWhenApplied = true;

  // Check if the tool action should be enabled. This is always called before
  // `action` so `action` does need to repeat these checks.
  // TODO add IToolEnabledInput here, will need to update all subclasses
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static enabled({ controller, selection }) {
    return !!selection && !selection.isLocked;
  }

  // Override this to customise the tool button's hint
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static hint(_: IToolEnabledInput) {
    return this.toolName;
  }

  // Check if the tool action should be available. Useful for hiding a menu
  // item based on context/selection.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static available(_: IToolAvailableInput) {
    return true;
  }

  static toolActionComplete({ controller }: IToolActionCompleteInput) {
    controller.undoManager.completeUndoGroup();
    controller.canvas.requestRenderAll();
  }

  static testid() {
    return `Tool-${makeTestId(this.toolName)}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static popover({ controller }): { [key: string]: any } {
    return {};
  }
}

// tool with standard action and button
class Tool extends BaseTool {
  // Invoke the tool action.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static action(_: IToolActionInput) {
    throw new Error('Implement action for tool');
  }

  // Render a component to render the toolbar button for the tool.
  static buttonComponent(): ToolButtonComponent | null {
    return null;
  }
}

export default Tool;
export { simpleToolButton };
